import React, { useEffect, useState } from 'react';
import ChatBot, { BotOptionsContext, MessagesContext } from 'react-chatbotify';
import { useSelector } from 'react-redux';
import '../../assets/css/chatbot.css';

import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import { makeStyles } from '@material-ui/core';
import { getChatBotService } from 'reduxLib/services/chatBotService';
import { useLAOGlobalContext } from 'context/LAOGlobalContext';
import { useDeepCompareEffect } from 'react-use';
import { isJsonParsable } from 'helpers';
import chatIcon from "../../assets/images/chatIcon-no-bg.png";
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles((theme) => ({
  table: {
    maxWidth: `${theme.spacing(5.5)}%`,
    margin: `${theme.spacing(0.125)}%`
  },
  tableCellHeader: {
    background: '#eef',
    border: '1px solid #aaf',
    textTransform: 'capitalize',
    width: `${theme.spacing(2.5)}%`
  },
  tableCell: {
    padding: `${theme.spacing(1)}px ${theme.spacing(2)}px`,
    width: `${theme.spacing(2.5)}%`,
    color: 'black',
    border: '1px solid #aaf'
  },
  chatBotTitle: {
    height: '100%',
    alignContent: 'center'
  }

}));


export const Title = () => {
  const classes = useStyles();
  return (
    <div className={classes.chatBotTitle} data-testid="chatTitle">
       {/* Latam Insights and Shipments Assistant */}
      <h2>LISA</h2>
    </div>
  );
};

const chatLanguages = {
  de: 'de-DE',
  en: 'en-US',
  es: 'es-ES',
  fr: 'fr-FR',
  pt: 'pt-PT',
}

const config = {
  chatButton: { icon: chatIcon },
  header: { title: <Title />, avatar: chatIcon }, 
  tooltip: { mode: 'NEVER' },
  footer: { text: '' },
  // chatHistory: { disabled: true },
  notification: { disabled: true },
  fileAttachment: { disabled: true },
  emoji: { disabled: true },
  chatWindow: {
    autoJumpToBottom: true,
    showScrollbar: true
  },
  theme: {
    // primaryColor: '',
    // secondaryColor: '',
    fontFamily: 'sans-serif'
  },
  voice: {
    disabled:  !(["local", 'development'].includes(process.env.REACT_APP_ENV)),
    // defaultToggledOn: false,
    // language: "en-US",
    // timeoutPeriod: 10000,
    autoSendDisabled: false,
    autoSendPeriod: 5000,
    // icon: voiceIcon,
  },
  advance: {
    useCustomMessages: true
  },
};

export const ResultComponent = () => {
  const classes = useStyles();
  const data = isJsonParsable(localStorage.getItem('chatData'));
  const dataKeys = data && data.length ? Object.keys(data[0]) : [];

  const isCurrencyField = (key, value) => {
    const keyLcase = key.toLowerCase();
    return parseInt(value) && (keyLcase.includes('dollar') || keyLcase.includes('value'))
  }

  const formatValue = (key, value) => {
    if(isCurrencyField(key, value)){
      const dollarVal =  new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
      }).format(value);
      return dollarVal.split('.')[0]
    }
    return value
  }

  return (
    data &&
    data.length && (
      <TableContainer>
        <Table className={classes.table} data-testid="chat-table">
          <TableHead>
            <TableRow>
              {dataKeys.map((v) => {
                return (
                  <TableCell className={classes.tableCellHeader} key={v}>
                    {v.replaceAll('_', ' ')}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((dataObj, index) => (
              <TableRow key={'row_' + index}>
                {dataKeys.map((v, i) => {
                  const alignmentClass = isCurrencyField(v, dataObj[v]) ? ' MuiTableCell-alignRight' : ''
                  return (
                    <TableCell
                      key={'row_' + index + '_col_' + i}
                      className={classes.tableCell + alignmentClass}
                      scope="row">
                      {formatValue(v, dataObj[v])}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    )
  );
};

export const ChatBotify = ({ region, user }) => {
  const { t } = useTranslation()
  const language = localStorage.getItem('i18nextLng');
  const [botOptions, setBotOptions] = useState({...config})
  const [messages, setMessages] = useState([]);

  useEffect(() => {
    if (messages.length <= 1) {
      setMessages([
        {
          content: t('chatInitialMessage'),
          sender: 'bot',
          type: 'string'
        }
      ]);
      config.voice.language = chatLanguages[language]
      setBotOptions(config)
    }
  }, [language]);

  const getMessage = async (params) => {
    const response = await getChatBotService(
      {
        username: user?.given_name,
        question: params.userInput
      },
      region
    ).catch(async (err) => {
      localStorage.setItem('chatData', '');
      await params.streamMessage(t('chatErrorMessage'));
    });

    if (response && response.translated_answer) {
      localStorage.setItem('chatData', JSON.stringify(response?.data_rows));
      await params.streamMessage(response.translated_answer);
    } else {
      localStorage.setItem('chatData', '');
      await params.streamMessage(t('chatErrorMessage'));
    }
  };

  const [flow, setFlow] = useState({
    start: {
      message: '',
      path: 'loop'
    },
    loop: {
      message: async (params) => {
        return getMessage(params);
      },
      render: <ResultComponent />,
      path: 'loop'
    }
  });

  return (
    <div data-testid="chatbot-container">
      <BotOptionsContext.Provider
        value={{ botOptions: botOptions, setBotOptions: setBotOptions }}>
        <MessagesContext.Provider
          value={{ messages: messages, setMessages: setMessages }}>
          <ChatBot flow={flow} options={botOptions} dataTestId="chatbot" />
        </MessagesContext.Provider>
      </BotOptionsContext.Provider>
    </div>
  );
};
